import { useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { formatPrice } from "@/lib/utils-app";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { toast } from "sonner";
import { Loader2, Star, MessageCircle, CreditCard, CheckCircle2 } from "lucide-react";
import { useEffect } from "react";

const PRO_STATUSES = [
  { v: "in_viaggio", label: "Sono in viaggio" },
  { v: "arrivato", label: "Sono arrivato" },
  { v: "iniziato", label: "Lavoro iniziato" },
  { v: "completato", label: "Lavoro completato" },
];

export function JobDetail() {
  const { jobId } = useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [priceDrawer, setPriceDrawer] = useState(false);
  const [reviewDrawer, setReviewDrawer] = useState(false);
  const [priceForm, setPriceForm] = useState({ new_price: "", reason: "" });
  const [review, setReview] = useState({ rating: 5, comment: "" });
  const isPro = user?.role === "professional";

  const { data: job, isLoading } = useQuery({
    queryKey: ["job", jobId],
    queryFn: async () => (await api.get(`/jobs/${jobId}`)).data,
  });

  const { data: reviewState } = useQuery({
    queryKey: ["job-review", jobId],
    queryFn: async () => (await api.get(`/reviews/job/${jobId}`)).data,
    retry: false,
  });

  useEffect(() => {
    if (params.get("payment") === "success" || params.get("session_id")) {
      api.get(`/payments/${jobId}/status`).then(({ data }) => {
        if (data.status === "paid" || data.paid) toast.success("Pagamento completato!");
        qc.invalidateQueries({ queryKey: ["job", jobId] });
      }).catch(() => {});
    }
  }, [params, jobId, qc]);

  const act = (fn, okMsg) => async () => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      qc.invalidateQueries({ queryKey: ["job", jobId] });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const setStatus = (status) => act(() => api.post(`/jobs/${jobId}/status`, { status }), "Stato aggiornato");
  const confirmJob = act(() => api.post(`/jobs/${jobId}/confirm`), "Lavoro confermato");
  const respondPrice = (approve) => act(() => api.post(`/jobs/${jobId}/price-change/respond`, { approve }), approve ? "Modifica approvata" : "Modifica rifiutata");

  const submitPriceChange = act(async () => {
    await api.post(`/jobs/${jobId}/price-change`, { new_price: Number(priceForm.new_price), reason: priceForm.reason });
    setPriceDrawer(false);
  }, "Proposta di modifica inviata");

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout-session", {
        job_id: jobId,
        return_base: `${window.location.origin}/lavori/${jobId}`,
      });
      const url = data.checkout_url || data.url;
      if (url) window.location.href = url;
      else toast.error("URL di pagamento non disponibile");
    } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };

  const submitReview = act(async () => {
    await api.post(`/jobs/${jobId}/review`, { rating: review.rating, comment: review.comment });
    setReviewDrawer(false);
    qc.invalidateQueries({ queryKey: ["job-review", jobId] });
  }, "Recensione inviata. Grazie!");

  if (isLoading) return <div className="px-4 pt-6 space-y-3"><Skeleton className="h-40 rounded-3xl" /><Skeleton className="h-24 rounded-3xl" /></div>;
  if (!job) return <div className="px-4 pt-6 text-sm text-slate-500" data-testid="job-not-found">Lavoro non trovato.</div>;

  const paid = job.payment_status === "paid" || job.paid;
  const pendingPriceChange = job.price_change && !job.price_change.resolved && !isPro;
  const canReview = !isPro && (job.status === "completato" || paid) && !reviewState?.reviewed && !reviewState?.exists;

  return (
    <div className="px-4 sm:px-6 pt-6 sm:pt-10 space-y-5 sm:max-w-2xl sm:mx-auto" data-testid="job-detail-page">
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-5 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <h1 className="font-heading text-xl font-semibold text-slate-900" data-testid="job-title">{job.title || job.request_title || "Lavoro"}</h1>
          <StatusBadge status={job.status} testid="job-detail-status" />
        </div>
        <div className="flex items-center justify-between">
          <p className="font-heading text-2xl font-bold text-slate-900" data-testid="job-price">{formatPrice(job.price)}</p>
          {paid && <span className="flex items-center gap-1 text-emerald-600 text-sm font-semibold" data-testid="paid-badge"><CheckCircle2 className="w-4 h-4" /> Pagato</span>}
        </div>
        <p className="text-xs text-slate-500 capitalize">{[job.category, job.zone || job.address].filter(Boolean).join(" · ")}</p>
      </div>

      {pendingPriceChange && (
        <div className="bg-amber-50 border border-amber-200 rounded-3xl p-4 space-y-3" data-testid="price-change-banner">
          <p className="text-sm font-semibold text-amber-800">Proposta di modifica prezzo: {formatPrice(job.price_change.new_price)}</p>
          <p className="text-xs text-amber-700">{job.price_change.reason}</p>
          <div className="flex gap-2">
            <Button data-testid="approve-price-btn" onClick={() => respondPrice(true)} disabled={busy} size="sm" className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">Approva</Button>
            <Button data-testid="reject-price-btn" onClick={() => respondPrice(false)} disabled={busy} size="sm" variant="outline" className="flex-1 rounded-full font-semibold">Rifiuta</Button>
          </div>
        </div>
      )}

      {isPro ? (
        <div className="space-y-3" data-testid="pro-actions">
          <h2 className="font-heading text-lg font-semibold text-slate-900">Aggiorna stato</h2>
          <div className="grid grid-cols-2 gap-2">
            {PRO_STATUSES.map((s) => (
              <Button key={s.v} data-testid={`status-${s.v}-btn`} onClick={() => setStatus(s.v)} disabled={busy || job.status === s.v}
                variant={job.status === s.v ? "default" : "outline"}
                className={`rounded-full font-semibold text-sm active:scale-95 transition-transform ${job.status === s.v ? "bg-whoo hover:bg-whoo-dark text-white" : ""}`}>
                {s.label}
              </Button>
            ))}
          </div>
          <Button data-testid="propose-price-change-btn" variant="outline" onClick={() => setPriceDrawer(true)} className="w-full rounded-full font-semibold">
            Proponi modifica prezzo
          </Button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="client-actions">
          {job.status === "in_attesa_conferma" && (
            <Button data-testid="confirm-job-btn" onClick={confirmJob} disabled={busy} className="w-full h-12 rounded-full bg-volt hover:bg-volt/90 text-white font-semibold">
              Conferma lavoro
            </Button>
          )}
          {!paid && ["completato", "confermato"].includes(job.status) && (
            <Button data-testid="pay-job-btn" onClick={pay} disabled={busy} className="w-full h-14 rounded-full bg-whoo hover:bg-whoo-dark text-white font-heading font-semibold active:scale-[0.98] transition-transform">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CreditCard className="w-5 h-5 mr-2" /> Paga {formatPrice(job.price)}</>}
            </Button>
          )}
          {canReview && (
            <Button data-testid="leave-review-btn" variant="outline" onClick={() => setReviewDrawer(true)} className="w-full h-12 rounded-full font-semibold">
              <Star className="w-4 h-4 mr-2" /> Lascia una recensione
            </Button>
          )}
        </div>
      )}

      <Link to="/chat">
        <Button data-testid="job-chat-btn" variant="outline" className="w-full rounded-full font-semibold">
          <MessageCircle className="w-4 h-4 mr-2" /> Apri chat
        </Button>
      </Link>

      <Drawer open={priceDrawer} onOpenChange={setPriceDrawer}>
        <DrawerContent className="max-w-md mx-auto rounded-t-3xl">
          <DrawerHeader className="text-left"><DrawerTitle className="font-heading">Modifica prezzo</DrawerTitle></DrawerHeader>
          <div className="px-4 pb-8 space-y-4">
            <div className="space-y-2">
              <Label>Nuovo prezzo (€)</Label>
              <Input data-testid="new-price-input" type="number" min="0.01" step="0.01" value={priceForm.new_price}
                onChange={(e) => setPriceForm((f) => ({ ...f, new_price: e.target.value }))}
                className="h-14 bg-slate-50 border-transparent rounded-2xl" />
            </div>
            <div className="space-y-2">
              <Label>Motivazione</Label>
              <Textarea data-testid="price-reason-input" rows={2} value={priceForm.reason}
                onChange={(e) => setPriceForm((f) => ({ ...f, reason: e.target.value }))}
                className="bg-slate-50 border-transparent rounded-2xl resize-none" />
            </div>
            <Button data-testid="submit-price-change-btn" onClick={submitPriceChange} disabled={busy} className="w-full h-14 rounded-full bg-whoo hover:bg-whoo-dark text-white font-semibold">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Invia proposta"}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>

      <Drawer open={reviewDrawer} onOpenChange={setReviewDrawer}>
        <DrawerContent className="max-w-md mx-auto rounded-t-3xl">
          <DrawerHeader className="text-left"><DrawerTitle className="font-heading">Lascia una recensione</DrawerTitle></DrawerHeader>
          <div className="px-4 pb-8 space-y-4">
            <div className="flex gap-2" data-testid="rating-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" data-testid={`star-${n}`} onClick={() => setReview((r) => ({ ...r, rating: n }))} className="active:scale-90 transition-transform">
                  <Star className={`w-8 h-8 ${n <= review.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                </button>
              ))}
            </div>
            <Textarea data-testid="review-comment-input" rows={3} value={review.comment}
              onChange={(e) => setReview((r) => ({ ...r, comment: e.target.value }))}
              className="bg-slate-50 border-transparent rounded-2xl resize-none" placeholder="Com'è andata l'esperienza?" />
            <Button data-testid="submit-review-btn" onClick={submitReview} disabled={busy} className="w-full h-14 rounded-full bg-whoo hover:bg-whoo-dark text-white font-semibold">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Invia recensione"}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
